import { useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "./../../assets/icons/close.svg?react";
import CheckIcon from "./../..//assets/icons/check.svg?react";
import { AiTrainingsUrl } from "../../utils/urls";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AiJobCreateSchema } from "../../utils/validations";
import {
  selectInstancesRegions,
  selectInstancesRegionsLoading,
  getRegionsAsync,
} from "../../store/instances/instancesSlice";
import {
  getDBSCatalogsAsync,
  selectDBSCatalogs,
  selectDBSCatalogsLoading,
} from "../../store/database/servicesSlice";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  Button,
  Card,
  cn,
  Flex,
  Input,
  Loading,
  Switcher,
  Typography,
  uuid,
} from "djuno-design";
import useLimitations from "../../hooks/useLimitations";
import { DBSCatalog } from "../../types/database";
import {
  getAiRegionsDataAsync,
  getAiResourceFlavorsAsync,
  selectAiRegionsData,
  selectAiRegionsDataLoading,
  selectAiResourceFlavors,
} from "../../store/ai/notebooks/notebooksSlice";
import {
  AiServiceAttachSshInput,
  AiServiceConfigInput,
  AiServiceFlavorInput,
  AiServiceRegionInput,
} from "../../components/ai-notebooks/create/components";
import { getInstancesSshKeysAsync } from "../../store/instances/instancesSSHkeysSlice";
import { datacenterDetails } from "../../utils/countries";
import { AiResourceUseCaseType } from "../../types/ai";
import {
  createAiJobAsync,
  getAiJobsAsync,
  getAiJobsPresetImageAsync,
  selectAiJobs,
  selectAiJobsActionLoading,
  selectAijobsLoading,
  selectSelectedPresetImages,
  selectSelectedPresetImagesLoading,
} from "../../store/ai/jobs/jobsSlice";
import { AiJobsPresetImagesInput } from "../../components/ai-trainings/create/components";
import { humanizeSize } from "../../utils/file";
import { currency } from "../../utils/currency";
import { useScrollToElement } from "../../hooks/useScrollToElement";

type CreateStep =
  | "region"
  | "image"
  | "dockerCommand"
  | "attachSSH"
  | "configure"
  | "validation";

const createSteps: Array<CreateStep> = [
  "region",
  "image",
  "dockerCommand",
  "attachSSH",
  "configure",
  "validation",
];

export type CreateJobFormType = {
  serviceName: string;
  selectedPresetImage: string;
  selectedRegionDataId: string;
  selectedResourceId: string;
  selectedResourceUnit: number;
  gitRepos?: Array<{ url: string; mountPath: string; permission: string }>;
  sshKeys?: Array<{ name: string; content: string; disabled: boolean }>;
  command?: (string | undefined)[] | null;
  customImageName?: string;
};

const AiJobCreatePage = () => {
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const jobs = useAppSelector(selectAiJobs);
  const jobsLoading = useAppSelector(selectAijobsLoading);

  const presetImages = useAppSelector(selectSelectedPresetImages);
  const presetImagesLoading = useAppSelector(selectSelectedPresetImagesLoading);

  const regions = useAppSelector(selectInstancesRegions);
  const regionsLoading = useAppSelector(selectInstancesRegionsLoading);
  const regionsData = useAppSelector(selectAiRegionsData);
  const regionsDataLoading = useAppSelector(selectAiRegionsDataLoading);

  const flavors = useAppSelector(selectAiResourceFlavors);

  const catalogs = useAppSelector(selectDBSCatalogs);
  const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);

  const actionLoading = useAppSelector(selectAiJobsActionLoading);

  // states
  const [expanded, setExpanded] = useState<CreateStep>("region");
  const [selectedUseCase, setSelectedUseCase] =
    useState<AiResourceUseCaseType>("cpu");
  const [filteredCatalogs, setFilteredCatalogs] = useState<Array<DBSCatalog>>(
    []
  );
  const [resourceSummary, setResourceSummary] = useState<{
    gpuCount: number;
    cpuCount: number;
    ramGB: number;
  }>({ gpuCount: 0, cpuCount: 0, ramGB: 0 });

  const [isCommand, setIsCommand] = useState(false);

  useEffect(() => {
    dispatch(getAiJobsPresetImageAsync({}));
  }, [dispatch]);

  //data for create from backup
  const returnUrl = query.get("returnUrl");

  const methods = useForm<CreateJobFormType>({
    resolver: yupResolver(AiJobCreateSchema(isCommand)),
    mode: "all",
    defaultValues: {
      serviceName: "",
      selectedPresetImage: "",
      selectedRegionDataId: "",
      selectedResourceId: "",
      selectedResourceUnit: 1,
      gitRepos: undefined,
      sshKeys: undefined,
      command: [], // Default to empty array
    },
  });

  const {
    // reset,
    setValue,
    handleSubmit,
    control,
    formState: { errors, isValid },
    trigger,
    watch,
    register,
  } = methods;

  // watch form values
  const appName = watch("serviceName");
  const selectedPresetImageId = watch("selectedPresetImage");
  const selectedRegionDataId = watch("selectedRegionDataId");
  const selectedResourceId = watch("selectedResourceId");
  const selectedResourceUnit = watch("selectedResourceUnit");
  const attachedGitRepos = watch("gitRepos");
  const attachedSshKeys = watch("sshKeys");
  // const customImageName = watch("customImageName");
  const command = watch("command") || [];

  const handleCommandMode = (value: boolean) => {
    setIsCommand(value);
  };

  useEffect(() => {
    trigger("sshKeys");
  }, [attachedSshKeys, trigger]);

  //get first data
  useEffect(() => {
    dispatch(getRegionsAsync());
    dispatch(getAiRegionsDataAsync({}));
    dispatch(getInstancesSshKeysAsync());
    dispatch(getDBSCatalogsAsync("ai-training"));
  }, [dispatch]);

  useEffect(() => {
    if (jobs.length === 0) dispatch(getAiJobsAsync({}));
  }, [dispatch, jobs.length]);

  // get resource (flavors) based on selected region
  useEffect(() => {
    if (selectedRegionDataId && selectedRegionDataId !== "")
      dispatch(getAiResourceFlavorsAsync({ region: selectedRegionDataId }));
  }, [dispatch, selectedRegionDataId]);

  // find selected PresetImage data
  const selectedPresetImage = useMemo(() => {
    return presetImages.find((f) => f.id === selectedPresetImageId);
  }, [presetImages, selectedPresetImageId]);

  const formatServiceName = (value: string) =>
    value.replace(/\s+/g, "-").replace(/[^a-z0-9-_]/g, "");

  useEffect(() => {
    if (selectedPresetImage?.name) {
      const rawName = `ai-training-${selectedPresetImage.name}-${uuid(6)}`;
      const sanitizedName = formatServiceName(rawName);
      setValue("serviceName", sanitizedName, { shouldValidate: true });
    }
  }, [setValue, selectedPresetImage]);

  // console.log("error", errors, isValid);

  // find selected region data
  const selectedRegion = useMemo(() => {
    return regions.find((r) => r.datacenterLocation === selectedRegionDataId);
  }, [regions, selectedRegionDataId]);

  // find selected datacenter
  const selectedDatacenter = useMemo(() => {
    return datacenterDetails.find((dc) => dc.location === selectedRegionDataId);
  }, [selectedRegionDataId]);

  // find selected freamwork data
  const selectedResource = useMemo(() => {
    return flavors.find((f) => f.id === selectedResourceId);
  }, [flavors, selectedResourceId]);

  // filter addons by selected engine and period
  useEffect(() => {
    if (catalogs) {
      const filtered = catalogs.filter((catalog) =>
        catalog.product?.startsWith("publiccloud-ai-training")
      );
      setFilteredCatalogs(filtered);
    }
  }, [catalogs]);

  const catalog = useMemo(() => {
    return filteredCatalogs.find((c) =>
      c.planCode.startsWith(
        "ai-training." + selectedResourceId + ".minute.consumption"
      )
    );
  }, [filteredCatalogs, selectedResourceId]);

  useEffect(() => {
    if (!selectedResource) return;

    const unit = selectedResourceUnit || 1;

    const updatedSummary = {
      gpuCount: selectedUseCase === "gpu" ? unit : 0,
      cpuCount: selectedUseCase === "cpu" ? unit : 0,
      ramGB: selectedResource.resourcesPerUnit.memory * unit,
    };

    setResourceSummary(updatedSummary);
  }, [selectedResource, selectedResourceUnit, selectedUseCase]);

  const handleEditRegionSelection = () => {
    setExpanded("region");
  };

  const handleEditImagerSelection = () => {
    setExpanded("image");
  };

  const handleEditDockerCommandSelection = () => {
    setExpanded("dockerCommand");
  };

  const handleEditAttachSshSelection = () => {
    setExpanded("attachSSH");
  };

  const handleEditConfigureSelection = () => {
    setExpanded("configure");
  };

  const handleSubmitForm = (data: any) => {
    // console.log(data);

    if (!selectedResource) return;
    if (!isValid) {
      console.log("Form errors:", errors);
      return;
    }

    const volumes = (attachedGitRepos || []).map((repo) => ({
      mountPath: repo.mountPath,
      permission: repo.permission,
      publicGit: {
        url: repo.url,
      },
    }));

    const sshPublicKeys = (attachedSshKeys || []).map((key) => key.content);

    if (!isCommand) {
      data.command = null; // set to null if isCommand is false
    }

    // calc resource data
    let resources: any = {
      flavor: selectedResource.id,
      //   memory: selectedResource.resourcesPerUnit.memory,
      //   publicNetwork: selectedResource.resourcesPerUnit.publicNetwork,
      //   privateNetwork: 0,
      //   ephemeralStorage: selectedResource.resourcesPerUnit.ephemeralStorage,
    };
    //for cpu types
    if (selectedUseCase === "cpu")
      resources = {
        ...resources,
        cpu: selectedResourceUnit,
      };

    //for gpu types
    if (selectedUseCase === "gpu")
      resources = {
        ...resources,
        gpu: selectedResourceUnit,
        gpuBrand: selectedResource.gpuInformation?.gpuBrand,
        gpuModel: selectedResource.gpuInformation?.gpuModel,
        gpuMemory: selectedResource.gpuInformation?.gpuMemory,
      };

    const apiData = {
      image: selectedPresetImageId,
      command: data.command,
      customImage: data.customImageName,
      name: appName,
      region: selectedRegionDataId,
      resources,
      volumes,
      unsecureHttp: false,
      sshPublicKeys,
    };

    // console.log(apiData);
    dispatch(createAiJobAsync({ data: apiData })).then((action) => {
      if (action.type === "ai-jobs/create/fulfilled") {
        methods.reset();
        navigate(returnUrl || AiTrainingsUrl);
      }
    });
  };

  const { numberOfAiJobsLimit, numberOfAiJobsLimitChecker } = useLimitations({
    cases: ["NumberOfAiJobs"],
  });

  const isExceedNumberOfAiJobs = useMemo(() => {
    return numberOfAiJobsLimitChecker(jobs.length);
  }, [jobs, numberOfAiJobsLimitChecker]);


  useScrollToElement({targetElementId:expanded});

  return (
    <>
      <div className="flex items-center justify-between h-16 px-6 sticky top-0 z-20 bg-white dark:bg-dark-1 border-b dark:border-dark-2">
        <div className="items-center justify-between flex flex-1 transition duration-150">
          <Typography.Text className="font-medium">
            Launch a new job
          </Typography.Text>
        </div>
        <div className="">
          <Button
            uiType="light"
            uiSize="small"
            onClick={() => navigate(returnUrl || AiTrainingsUrl)}
            className="group"
          >
            <CloseIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
          </Button>
        </div>
      </div>

      {catalogsLoading && (
        <Flex
          items="center"
          justify="center"
          className="h-full w-full min-h-[calc(100vh-128px)]"
        >
          <Loading borderSize={2} />
        </Flex>
      )}
      {!catalogsLoading && (
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className="mt-10 w-full px-6 flex flex-col lg:flex-row pb-24 gap-8 relative">
            <div className="w-full flex flex-col">
              {/* Choose a region */}
              <Accordion
                expanded={expanded === "region"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="region"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 0 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Location selected: {selectedRegionDataId}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditRegionSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "region",
                          }
                        )}
                      >
                        1. Choose a region
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiServiceRegionInput
                      regions={regions}
                      regionsData={regionsData}
                      control={control}
                      loading={regionsLoading || regionsDataLoading}
                      errorMessage={errors.selectedRegionDataId?.message}
                      // disabled={!!instanceSnapshot}
                    />
                  </div>
                  <Button
                    id="2th-next-button"
                    disabled={selectedRegionDataId === ""}
                    onClick={() => setExpanded("image")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* Enter the Docker image*/}
              <Accordion
                expanded={expanded === "image"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="freamwork"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 1 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 flex-shrink-0 text-primary-500" />
                          Docker image selected: {selectedPresetImage?.name}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditImagerSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "image",
                          }
                        )}
                      >
                        2. Enter the Docker image
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiJobsPresetImagesInput
                      control={control}
                      presetImages={presetImages}
                      errorMessage={errors.selectedPresetImage?.message}
                      loading={presetImagesLoading}
                    />
                  </div>
                  <Button
                    disabled={selectedPresetImageId === ""}
                    onClick={() => setExpanded("dockerCommand")}
                    id="3th-next-button"
                    className="mt-5"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* Enter the Docker command to launch in the container (optional) */}
              <Accordion
                expanded={expanded === "dockerCommand"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="resources"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 2 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Docker command to launch in the container
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditDockerCommandSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "dockerCommand",
                          }
                        )}
                      >
                        3. Enter the Docker command to launch in the container
                        (optional)
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <Flex className="gap-1" direction="col" justify="start">
                      <Typography.Text size="xs">
                        Specify the command to run (optional)
                      </Typography.Text>
                      <Switcher
                        value={isCommand}
                        onChange={handleCommandMode}
                        loading={actionLoading}
                      />

                      {isCommand && (
                        <Input
                          value={command.join(" ")}
                          onChange={(e) => {
                            setValue(
                              "command",
                              e.target.value ? e.target.value.split(" ") : []
                            );
                          }}
                          className="!w-1/2"
                        />
                      )}
                    </Flex>
                  </div>
                  <Button
                    disabled={
                      isCommand &&
                      (!command ||
                        command.length === 0 ||
                        command.some((cmd) => cmd && !cmd.trim()) || // Check if cmd is defined before calling trim
                        !!errors.command)
                    }
                    onClick={() => setExpanded("attachSSH")}
                    id="4th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* attach SSH */}
              <Accordion
                expanded={expanded === "attachSSH"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="attachSSH"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 3 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Attached SSH public keys:{" "}
                          {attachedSshKeys?.length || 0}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditAttachSshSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "attachSSH",
                          }
                        )}
                      >
                        4. Attach SSH public keys
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiServiceAttachSshInput
                      control={control}
                      setValue={setValue}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      errors.sshKeys &&
                      errors.sshKeys.length !== undefined &&
                      errors.sshKeys.length > 0
                    }
                    onClick={() => setExpanded("configure")}
                    id="attachSSH"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* Configure your job*/}
              <Accordion
                expanded={expanded === "configure"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="configure"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 4 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Resource selected: {selectedResourceId} - x
                          {selectedResourceUnit}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditConfigureSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "configure",
                          }
                        )}
                      >
                        5. Configure your job
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiServiceConfigInput
                      control={control}
                      errors={errors}
                      register={register}
                      withoutLabels={false}
                      serviceNameInputLabel="Choose job name"
                    />

                    <AiServiceFlavorInput
                      control={control}
                      selectedUseCase={selectedUseCase}
                      setSelectedUseCase={setSelectedUseCase}
                      catalogs={filteredCatalogs}
                      catalogsLoading={catalogsLoading}
                      errorMessage={errors.selectedResourceId?.message}
                      useCasePrefix="AI Training"
                    />
                  </div>

                  <Button
                    disabled={
                      !selectedResourceId?.trim() ||
                      !selectedUseCase ||
                      !appName?.trim()
                    }
                    onClick={() => setExpanded("validation")}
                    id="3th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* Detail validation */}
              <Accordion
                expanded={expanded === "validation"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="review"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 5 ? (
                      <div className="flex flex-col gap-5 w-full">
                        <div className="flex w-full items-center justify-between">
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Detail validation
                          </Typography.Text>
                        </div>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "validation",
                          }
                        )}
                      >
                        6. Detail validation
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5 flex flex-col md:flex-row gap-2">
                    <Card title="Job summary">
                      <Flex direction="col" className="w-full gap-5 px-5">
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Docker Image
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {selectedPresetImage?.name}
                          </Typography.Text>
                        </Flex>

                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">Region</Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {selectedRegion ? (
                              <>
                                {selectedRegion.countryCode.toUpperCase()} -{" "}
                                {selectedDatacenter?.title} (
                                {selectedRegion.name})
                              </>
                            ) : (
                              <> {selectedRegionDataId}</>
                            )}
                          </Typography.Text>
                        </Flex>
                      </Flex>
                    </Card>
                    <Card title="Review">
                      <Flex direction="col" className="w-full gap-5 px-5">
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Number of GPUs
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {resourceSummary.gpuCount}
                          </Typography.Text>
                        </Flex>

                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Number of vCPUs
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {resourceSummary.cpuCount}
                          </Typography.Text>
                        </Flex>

                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Volume of RAM (in GB)
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {humanizeSize(resourceSummary.ramGB).join("B")}{" "}
                          </Typography.Text>
                        </Flex>

                        {catalog && (
                          <Flex direction="col" className="w-full">
                            <Typography.Text size="sm">Price</Typography.Text>
                            <Typography.Text size="sm" uiType="secondary">
                              {currency(
                                selectedResourceUnit *
                                  catalog.pricings[0].price *
                                  60
                              )}{" "}
                              resource/hour
                            </Typography.Text>
                          </Flex>
                        )}
                      </Flex>
                    </Card>
                  </div>

                  <Flex>
                    <Button
                      type="submit"
                      disabled={
                        !isValid || isExceedNumberOfAiJobs || actionLoading
                      }
                      uiType="primary"
                      loading={actionLoading || jobsLoading}
                      tooltip={{
                        content: isExceedNumberOfAiJobs
                          ? numberOfAiJobsLimit?.LimitationMessage
                          : "",
                        className: "!text-sm",
                      }}
                    >
                      Create
                    </Button>
                  </Flex>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default AiJobCreatePage;
